import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Services.css';

const HARBOR_DEMO_URL = '/portfolio/practice';
const LUXE_DEMO_URL   = '/portfolio/starter';
const BREWED_DEMO_URL = '/portfolio/commerce';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mqewrgkd';

const PACKAGES = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$1,499',
    priceSub: 'flat',
    tagline: 'For solo service businesses with a single, clear offer.',
    bullets: [
      'One-page website with clear sections (hero, about, services, gallery, contact)',
      'Mobile-friendly, responsive design',
      'Static image gallery or portfolio grid',
      'Contact section with email, phone, or inquiry form',
      'Connected to your domain',
      'Copywriting included for all core pages. Bring your ideas, notes, or rough copy and we shape the final words',
      'Structured feedback at defined project milestones, with a final review before launch',
      'Post-launch walkthrough included',
    ],
    timeline: '3 weeks from content handoff',
    seeItBuilt: LUXE_DEMO_URL,
  },
  {
    id: 'business',
    name: 'Business',
    price: '$2,499',
    priceSub: 'flat',
    tagline: 'For solo or small-team businesses that need pages, structure, and a real inquiry flow.',
    bullets: [
      '4 to 6 page website',
      'Booking or scheduling setup (Calendly, Cal.com, or similar)',
      'Structured intake form for incoming leads',
      'Expanded gallery, portfolio, or results section',
      'FAQ and policies pages',
      'Copywriting included for all core pages. Bring your ideas, notes, or rough copy and we shape the final words',
      'Structured feedback at defined project milestones, with a final review before launch',
      'Post-launch walkthrough included',
    ],
    timeline: '5 weeks from content handoff',
  },
  {
    id: 'practice',
    name: 'Practice',
    price: '$3,999',
    priceSub: 'flat',
    tagline: 'For multi-staff service businesses: therapy practices, salons with multiple stylists, consultancies, fitness studios.',
    bullets: [
      '6 to 8 pages including individual staff/provider profiles',
      <><strong>Custom multi-step booking</strong>, service to staff to date to time</>,
      <><strong>Tailored intake forms</strong> that adapt to staff specialty or service type</>,
      'Per-staff availability and pricing',
      'Filterable staff directory',
      'Optional Stripe payment integration',
      'Gallery, FAQ, and policies pages',
      'Copywriting included for all core pages. Bring your ideas, notes, or rough copy and we shape the final words',
      'Structured feedback at defined project milestones, with a final review before launch',
      'Post-launch walkthrough included',
    ],
    timeline: '6 weeks from content handoff',
    seeItBuilt: HARBOR_DEMO_URL,
  },
  {
    id: 'ecommerce',
    name: 'Commerce',
    price: '$5,499',
    priceSub: 'flat',
    tagline: 'A fully built e-commerce store with product pages, cart, Stripe checkout, and order flow.',
    bullets: [
      'Up to 50 products included at launch; additional products available as an add-on',
      'Category organization with filters and sorting',
      'Individual product pages with related items and reviews section',
      'Shopping cart with quantity controls and promo code support',
      'Stripe-integrated checkout with order summary and confirmation screen',
      'Automated order confirmation flow',
      'Contact page with location, store hours, and FAQ',
      'Homepage with hero, featured products, and category grid',
      'Mobile-optimized throughout: every page, every interaction',
      'Copywriting included for all core pages. Bring your ideas, notes, or rough copy and we shape the final words',
      'Structured feedback at defined project milestones, with a final review before launch',
      'Post-launch walkthrough included',
    ],
    timeline: '8 weeks from content handoff',
    seeItBuilt: BREWED_DEMO_URL,
  },
];

const ADDONS = [
  {
    category: 'Identity',
    name: 'Brand refresh',
    price: '$1,300',
    priceNote: 'flat',
    desc: 'A full visual redesign for a site we already built, for when you want a new look down the line. We explore a few directions, then refresh your colors, typography, and overall visual direction, with optional logo updates, and apply the new design across your live site. Includes two revision rounds. You receive the final palette, type system, and brand files at the end.',
    icon: '✦',
  },
  {
    category: 'Identity',
    name: 'Logo design or refresh',
    price: '$250',
    desc: 'A clean logo built to work across your site, social, and print.',
    icon: '◈',
  },
  {
    category: 'Identity',
    name: 'Social media asset set',
    price: '$300',
    desc: 'Permanent profile and header graphics aligned with your site, plus branded launch posts and stories to announce when you go live.',
    icon: '⊹',
  },
  {
    category: 'Functionality',
    name: 'AI chatbot',
    price: 'from $1,200',
    priceNote: 'one-time build',
    altPrice: '+ $150 / mo',
    altPriceNote: 'maintenance & API costs',
    desc: 'A custom-built chatbot trained on your business. It answers client questions, qualifies leads, and collects contact info 24/7. Built on the Anthropic API. Quoted per project based on complexity.',
    icon: '◎',
    highlight: 'top',
  },
  {
    category: 'Launch',
    name: 'Calendly setup',
    price: '$350',
    priceNote: 'one-time',
    desc: 'Calendly or Cal.com configured to match your services, embedded on your site, with calendar syncing and branded confirmation emails. The simpler alternative to a custom booking system.',
    icon: '⊕',
  },
  {
    category: 'Launch',
    name: 'SEO setup',
    price: '$500',
    priceNote: 'one-time',
    desc: 'One-time technical configuration: meta tags, sitemap submission, Google Search Console setup, image optimization, and structured data. Sets the foundation for organic traffic, which builds over 3 to 6 months. This is setup, not ongoing SEO work.',
    icon: '◉',
  },
  {
    category: 'Functionality',
    name: 'POS setup',
    price: 'from $200',
    priceNote: 'one-time',
    desc: 'Available with Commerce builds. No point-of-sale system yet? We select the right platform, load your full product catalog, configure taxes and payments, and connect it directly to your new site so inventory stays in sync.',
    descBreakdown: [
      { label: 'Up to 50 products', value: 'Included with Commerce' },
      { label: '51 to 150 products', value: '$200' },
      { label: '151 to 300 products', value: '$400' },
      { label: '300+ products', value: 'Quoted' },
    ],
    icon: '⊞',
    highlight: 'middle',
  },
  {
    category: 'Content',
    name: 'Additional page',
    price: 'from $150 / page',
    desc: 'For services, locations, or offers that need their own space.',
    icon: '▦',
  },
  {
    category: 'Launch',
    name: 'Priority rush delivery',
    price: '+20% project total',
    desc: 'A faster timeline when schedule space allows.',
    icon: '◐',
    noCta: true,
  },
  {
    category: 'Functionality',
    name: 'Custom booking system',
    price: '$1,000',
    priceNote: 'one-time build',
    altPrice: 'single provider',
    altPriceNote: 'scope',
    desc: 'A real booking flow built into your site, not an embedded Calendly. Service selection, date and time picker, calendar sync, and automated email confirmations. For multi-staff booking with per-provider availability, see the Practice tier.',
    icon: '⊛',
    highlight: 'bottom',
  },
];

const MAINTENANCE_PLANS = [
  {
    name: 'Light',
    price: '$75',
    tagline: 'For occasional touch-ups.',
    bullets: [
      'Up to 2 content updates per month',
      'Requests fulfilled within 3 to 5 business days',
      'Monthly site check: broken links, form functionality, load speed',
      'Submit requests by email; we follow up within 24 hours',
    ],
  },
  {
    name: 'Standard',
    price: '$125',
    tagline: 'Steady month-to-month support.',
    bullets: [
      'Up to 4 content updates per month',
      'Requests fulfilled within 3 to 5 business days',
      'Monthly cleanup and light performance check',
      'Small design tweaks (labels, spacing, minor layout)',
    ],
  },
  {
    name: 'Premium',
    price: '$175',
    tagline: 'For brands that update frequently.',
    bullets: [
      'Up to 8 content updates per month',
      'Priority turnaround: within 1 to 2 business days',
      'Monthly backup sent to you, plus a concise site health summary',
      'Small design tweaks and layout adjustments',
    ],
  },
];

const ESTIMATOR_TIERS = [
  { id: 'starter',   label: 'Starter',   base: 1499 },
  { id: 'business',  label: 'Business',  base: 2499 },
  { id: 'practice',  label: 'Practice',  base: 3999 },
  { id: 'ecommerce', label: 'Commerce',  base: 5499 },
];

const ESTIMATOR_ADDONS = [
  { id: 'logo',     label: 'Logo design',           price: 250,  note: null },
  { id: 'social',   label: 'Social asset set',      price: 300,  note: null },
  { id: 'chatbot',  label: 'AI chatbot',            price: 1200, note: 'from' },
  { id: 'booking',  label: 'Custom booking system', price: 1000, note: null },
  { id: 'calendly', label: 'Calendly setup',        price: 350,  note: null },
  { id: 'seo',      label: 'SEO setup',             price: 500,  note: null },
  { id: 'pos',      label: 'POS setup',             price: 200,  note: 'from' },
];

// Helper component: renders a single addon card (regular or highlight)
function AddonCard({ item, isHighlight }) {
  if (isHighlight) {
    return (
      <div className="svc-addon-card svc-addon-card-highlight">
        <div className="svc-addon-card-content">
          <div className="svc-addon-card-top">
            <span className="svc-addon-card-icon">{item.icon}</span>
            <span className="svc-addon-card-category">{item.category}</span>
          </div>
          <h5 className="svc-addon-card-name">{item.name}</h5>
          <p className="svc-addon-card-desc">{item.desc}</p>
          {item.descBreakdown && (
            <ul className="svc-addon-card-breakdown">
              {item.descBreakdown.map((row) => (
                <li key={row.label} className="svc-addon-card-breakdown-row">
                  <span className="svc-addon-card-breakdown-label">{row.label}</span>
                  <span className="svc-addon-card-breakdown-value">{row.value}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="svc-addon-card-pricing">
          <div className="svc-addon-card-price-row">
            <span className="svc-addon-card-price">{item.price}</span>
            {item.priceNote && <span className="svc-addon-card-price-note">{item.priceNote}</span>}
          </div>
          {item.altPrice && (
            <div className="svc-addon-card-price-row svc-addon-card-price-row-alt">
              <span className="svc-addon-card-price">{item.altPrice}</span>
              {item.altPriceNote && <span className="svc-addon-card-price-note">{item.altPriceNote}</span>}
            </div>
          )}
        </div>
        <Link to="/contact" className="svc-btn svc-btn-ghost svc-btn-arrow svc-addon-cta">
          Ask about this
        </Link>
      </div>
    );
  }

  return (
    <div className="svc-addon-card">
      <div className="svc-addon-card-top">
        <span className="svc-addon-card-icon">{item.icon}</span>
        <span className="svc-addon-card-category">{item.category}</span>
      </div>
      <h5 className="svc-addon-card-name">{item.name}</h5>
      <p className="svc-addon-card-desc">{item.desc}</p>
      {item.descBreakdown && (
        <ul className="svc-addon-card-breakdown">
          {item.descBreakdown.map((row) => (
            <li key={row.label} className="svc-addon-card-breakdown-row">
              <span className="svc-addon-card-breakdown-label">{row.label}</span>
              <span className="svc-addon-card-breakdown-value">{row.value}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="svc-addon-card-pricing">
        <div className="svc-addon-card-price-row">
          <span className="svc-addon-card-price">{item.price}</span>
          {item.priceNote && <span className="svc-addon-card-price-note">{item.priceNote}</span>}
        </div>
        {item.altPrice && (
          <div className="svc-addon-card-price-row svc-addon-card-price-row-alt">
            <span className="svc-addon-card-price">{item.altPrice}</span>
            {item.altPriceNote && <span className="svc-addon-card-price-note">{item.altPriceNote}</span>}
          </div>
        )}
      </div>
      {!item.noCta && (
        <Link to="/contact" className="svc-btn svc-btn-ghost svc-btn-arrow svc-addon-cta">
          Ask about this
        </Link>
      )}
    </div>
  );
}

// Live cursor spotlight on dark cards
function attachSpotlight(card) {
  const spot = card.querySelector('.svc-package-spotlight');
  if (!spot) return () => {};
  let raf = null, nx = 50, ny = 50;
  const apply = () => {
    spot.style.background = `radial-gradient(circle 360px at ${nx}% ${ny}%, rgba(43,165,165,0.28) 0%, rgba(43,165,165,0.10) 35%, transparent 65%)`;
    raf = null;
  };
  const onMove = (e) => {
    const r = card.getBoundingClientRect();
    nx = ((e.clientX - r.left) / r.width) * 100;
    ny = ((e.clientY - r.top) / r.height) * 100;
    if (raf === null) raf = requestAnimationFrame(apply);
  };
  const onLeave = () => {
    nx = 50; ny = 50;
    if (raf === null) raf = requestAnimationFrame(apply);
  };
  card.addEventListener('mousemove', onMove);
  card.addEventListener('mouseleave', onLeave);
  return () => {
    card.removeEventListener('mousemove', onMove);
    card.removeEventListener('mouseleave', onLeave);
    if (raf !== null) cancelAnimationFrame(raf);
  };
}

// Modal: collect email + phone, send the configured estimate to Formspree
function QuoteRequestModal({ open, onClose, payload }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const dialogRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStatus('idle');
      setErrorMsg('');
    } else {
      setEmail('');
      setPhone('');
      setNotes('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const input = dialogRef.current?.querySelector('input[type="email"]');
      if (input) input.focus();
    }, 60);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isValidPhone = (v) => v.replace(/\D/g, '').length >= 7;
  const canSubmit = isValidEmail(email) && isValidPhone(phone) && status !== 'sending';

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    setStatus('sending');
    setErrorMsg('');

    const fmt = (n) => '$' + n.toLocaleString();

    const body = {
      _subject: `Quote request: ${payload.tierName} · ${fmt(payload.total)}`,
      email,
      phone,
      notes,
      source: 'Services Estimator',
      tier: payload.tierName,
      tier_base: fmt(payload.tierBase),
      extra_pages: payload.extraPages,
      extra_pages_total: fmt(payload.pageTotal),
      addons: payload.addonLabels.length ? payload.addonLabels.join(', ') : 'None',
      addons_total: fmt(payload.addonTotal),
      timeline: payload.rush ? 'Priority rush (+20%)' : 'Standard',
      rush_amount: fmt(payload.rushAmount),
      subtotal: fmt(payload.subtotal),
      estimated_total: fmt(payload.total),
    };

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        const data = await res.json().catch(() => ({}));
        setStatus('error');
        setErrorMsg(data.error || 'Something went wrong. Please try again or email syeda@layerandco.com.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('Network error. Please try again or email syeda@layerandco.com.');
    }
  };

  return (
    <div
      className="svc-modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        className="svc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="svc-modal-title"
      >
        <button
          type="button"
          className="svc-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {status !== 'success' && (
          <>
            <p className="svc-eyebrow">Request a written quote</p>
            <h3 id="svc-modal-title">
              Where should we <em>send it?</em>
            </h3>
            <p className="svc-modal-blurb">
              Drop your email and phone. We&apos;ll send a fixed, itemized quote within two business days, based on the configuration below.
            </p>

            <div className="svc-modal-summary">
              <div className="svc-modal-summary-row">
                <span>Tier</span>
                <span>{payload.tierName}</span>
              </div>
              {payload.extraPages > 0 && (
                <div className="svc-modal-summary-row">
                  <span>Extra pages</span>
                  <span>+{payload.extraPages} (${payload.pageTotal.toLocaleString()})</span>
                </div>
              )}
              {payload.addonLabels.length > 0 && (
                <div className="svc-modal-summary-row">
                  <span>Add-ons</span>
                  <span>{payload.addonLabels.join(', ')}</span>
                </div>
              )}
              {payload.rush && (
                <div className="svc-modal-summary-row">
                  <span>Timeline</span>
                  <span>Priority rush (+20%)</span>
                </div>
              )}
              <div className="svc-modal-summary-row svc-modal-summary-total">
                <span>Estimated total</span>
                <span>${payload.total.toLocaleString()}</span>
              </div>
            </div>

            <form className="svc-modal-form" onSubmit={handleSubmit} noValidate>
              <label className="svc-modal-field">
                <span>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  required
                  autoComplete="email"
                />
              </label>

              <label className="svc-modal-field">
                <span>Phone</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 555-5555"
                  required
                  autoComplete="tel"
                />
              </label>

              <label className="svc-modal-field">
                <span>Anything else? <em>Optional</em></span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Business name, timeline, anything we should know."
                  rows={3}
                />
              </label>

              {status === 'error' && (
                <p className="svc-modal-error">{errorMsg}</p>
              )}

              <div className="svc-modal-actions">
                <button
                  type="button"
                  className="svc-btn svc-btn-ghost"
                  onClick={onClose}
                  disabled={status === 'sending'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="svc-btn svc-btn-primary svc-btn-arrow"
                  disabled={!canSubmit}
                >
                  {status === 'sending' ? 'Sending…' : 'Send request'}
                </button>
              </div>
            </form>
          </>
        )}

        {status === 'success' && (
          <div className="svc-modal-success">
            <div className="svc-modal-success-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <path d="M4 12 L10 18 L20 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3>Request <em>received.</em></h3>
            <p>
              Thanks. We&apos;ll review your configuration and reply within two business days with a fixed, itemized quote.
            </p>
            <button type="button" className="svc-btn svc-btn-primary svc-btn-arrow" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Estimator() {
  const [tier, setTier] = useState('business');
  const [extraPages, setExtraPages] = useState(0);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [rush, setRush] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const tierBase = ESTIMATOR_TIERS.find((t) => t.id === tier)?.base ?? 2499;
  const tierName = ESTIMATOR_TIERS.find((t) => t.id === tier)?.label ?? '';
  const addonTotal = ESTIMATOR_ADDONS
    .filter((a) => selectedAddons.includes(a.id))
    .reduce((sum, a) => sum + a.price, 0);
  const addonLabels = ESTIMATOR_ADDONS
    .filter((a) => selectedAddons.includes(a.id))
    .map((a) => a.label);
  const pageTotal = extraPages * 150;
  const subtotal = tierBase + addonTotal + pageTotal;
  const rushAmount = rush ? Math.round(subtotal * 0.2) : 0;
  const total = subtotal + rushAmount;
  const fmt = (n) => '$' + n.toLocaleString();

  const toggleAddon = (id) => {
    setSelectedAddons((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const modalPayload = {
    tierName,
    tierBase,
    extraPages,
    pageTotal,
    addonLabels,
    addonTotal,
    rush,
    rushAmount,
    subtotal,
    total,
  };

  return (
    <section id="estimator" className="svc-section">
      <div className="svc-container">
        <div className="svc-section-header">
          <p className="svc-eyebrow">Quick estimate</p>
          <h2>Build your package, <em>live.</em></h2>
          <p>Indicative only. A written quote with exact numbers comes before any work begins.</p>
        </div>

        <div className="svc-estimator">
          <div className="svc-estimator-total">
            <div>
              <span className="svc-estimator-total-label">Estimated total · {tierName} configuration</span>
              <span className="svc-estimator-tier-name">{tierName}{rush ? ' · priority' : ''}{selectedAddons.length ? ` · ${selectedAddons.length} add-on${selectedAddons.length === 1 ? '' : 's'}` : ''}</span>
            </div>
            <span className="svc-estimator-total-amount">{fmt(total)}</span>
          </div>

          <div className="svc-estimator-body">
            <div className="svc-estimator-row">
              <span className="svc-estimator-row-label">
                Tier
                <span className="svc-estimator-row-note">choose one</span>
              </span>
              <div className="svc-estimator-options">
                {ESTIMATOR_TIERS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`svc-estimator-opt${tier === t.id ? ' is-active' : ''}`}
                    onClick={() => setTier(t.id)}
                  >
                    {t.label}
                    <span className="svc-estimator-opt-price">{fmt(t.base)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="svc-estimator-row">
              <span className="svc-estimator-row-label">
                Extra pages
                <span className="svc-estimator-row-note">$150 each</span>
              </span>
              <div className="svc-estimator-options">
                {[0, 1, 2, 3, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`svc-estimator-opt${extraPages === n ? ' is-active' : ''}`}
                    onClick={() => setExtraPages(n)}
                  >
                    {n === 0 ? 'None' : `+${n}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="svc-estimator-row">
              <span className="svc-estimator-row-label">
                Add-ons
                <span className="svc-estimator-row-note">multi-select</span>
              </span>
              <div className="svc-estimator-options">
                {ESTIMATOR_ADDONS.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`svc-estimator-opt${selectedAddons.includes(a.id) ? ' is-active' : ''}`}
                    onClick={() => toggleAddon(a.id)}
                  >
                    {a.label}
                    <span className="svc-estimator-opt-price">
                      {a.note === 'from' ? 'from ' : ''}{fmt(a.price)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="svc-estimator-row">
              <span className="svc-estimator-row-label">
                Timeline
                <span className="svc-estimator-row-note">when do you launch</span>
              </span>
              <div className="svc-estimator-options">
                <button
                  type="button"
                  className={`svc-estimator-opt${!rush ? ' is-active' : ''}`}
                  onClick={() => setRush(false)}
                >
                  Standard
                </button>
                <button
                  type="button"
                  className={`svc-estimator-opt${rush ? ' is-active' : ''}`}
                  onClick={() => setRush(true)}
                >
                  Priority rush <span className="svc-estimator-opt-price">+20%</span>
                </button>
              </div>
            </div>
          </div>

          <div className="svc-estimator-foot">
            <p className="svc-estimator-disclaimer">
              A rough estimate. Your actual quote is fixed, itemized, and sent in writing before anything starts.
            </p>
            <button
              type="button"
              className="svc-btn svc-btn-primary svc-btn-arrow"
              onClick={() => setModalOpen(true)}
            >
              Get a written quote
            </button>
          </div>
        </div>
      </div>

      <QuoteRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        payload={modalPayload}
      />
    </section>
  );
}

function Services() {
  const featuredCardRef = useRef(null);
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const el = document.getElementById(id);
    if (!el) return;
    const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    return () => clearTimeout(t);
  }, [hash]);

  useEffect(() => {
    let cleanups = [];
    let retry = null;
    let tries = 0;
    const attach = () => {
      const cards = document.querySelectorAll('.svc-package');
      if (cards.length === 0 && tries < 10) {
        tries += 1;
        retry = setTimeout(attach, 100);
        return;
      }
      cards.forEach((c) => cleanups.push(attachSpotlight(c)));
    };
    retry = setTimeout(attach, 50);
    return () => {
      if (retry) clearTimeout(retry);
      cleanups.forEach((fn) => fn());
      cleanups = [];
    };
  }, []);

  // Bucket the addons by position for cleaner rendering
  const topAddons      = ADDONS.filter(i => !i.highlight).slice(0, 3);
  const middleAddons   = ADDONS.filter(i => !i.highlight).slice(3);
  const topHighlight    = ADDONS.find(i => i.highlight === 'top');
  const middleHighlight = ADDONS.find(i => i.highlight === 'middle');
  const bottomHighlight = ADDONS.find(i => i.highlight === 'bottom');

  return (
    <main className="svc-page">

      {/* HEADER */}
      <section className="svc-header-compact">
        <div className="svc-header-compact-grid" aria-hidden="true" />
        <div className="svc-container">
          <div className="svc-header-compact-row">
            <div>
              <p className="svc-eyebrow">Services &amp; packages</p>
              <h1>Four tiers.<br /><em>Flat</em> pricing.</h1>
            </div>
            <div className="svc-header-compact-meta">
              <div className="svc-header-compact-meta-row">
                <span>Tiers</span>
                <span>Starter · Business · Practice · Commerce</span>
              </div>
              <div className="svc-header-compact-meta-row">
                <span>From</span>
                <span>$1,499</span>
              </div>
              <div className="svc-header-compact-meta-row">
                <span>Timeline</span>
                <span>3 to 8 weeks</span>
              </div>
              <div className="svc-header-compact-meta-row">
                <span>Quote</span>
                <span>fixed, in writing</span>
              </div>
              <div className="svc-header-compact-meta-row">
                <span>Location</span>
                <span>New York · remote anywhere</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="svc-section">
        <div className="svc-container">
          <div className="svc-packages-row">
            {PACKAGES.map((pkg, idx) => {
              const numerals = ['I', 'II', 'III', 'IV'];
              return (
              <article
                key={pkg.id}
                id={pkg.id}
                ref={pkg.featured ? featuredCardRef : null}
                className="svc-package"
              >
                <div className="svc-package-spotlight" aria-hidden="true" />
                <span className="svc-package-numeral" aria-hidden="true">{numerals[idx]}</span>

                <header className="svc-package-top">
                  <span className="svc-package-tier-tag">Tier 0{idx + 1}</span>
                  <h3>{pkg.name}</h3>
                </header>

                <div className="svc-package-pricerow">
                  <div className="svc-package-pricerow-main">
                    <span className="svc-package-price">{pkg.price}</span>
                    {pkg.priceSub && <span className="svc-package-price-sub">{pkg.priceSub}</span>}
                  </div>
                  <span className="svc-package-timeline-pill">{pkg.timeline}</span>
                </div>

                <p className="svc-package-tagline">{pkg.tagline}</p>

                <div className="svc-package-divider">
                  <span>Includes</span>
                  <span>{String(pkg.bullets.length).padStart(2, '0')} items</span>
                </div>

                <ul className="svc-package-bullets">
                  {pkg.bullets.map((b, i) => (
                    <li key={i} className="svc-package-bullet">
                      <span className="svc-package-bullet-check" aria-hidden="true">
                        <svg viewBox="0 0 12 12" width="11" height="11"><path d="M2 6.5 L5 9.5 L10 3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="svc-package-actions">
                  <Link to="/contact" className="svc-btn svc-btn-primary svc-btn-arrow">
                    Get in touch
                  </Link>
                  {pkg.seeItBuilt && (
                    <Link to={pkg.seeItBuilt} className="svc-btn svc-btn-ghost svc-btn-arrow">
                      See it built
                    </Link>
                  )}
                </div>
              </article>
              );
            })}
          </div>
          <p className="svc-package-disclosure">
            Out-of-scope work is identified and quoted before any work begins.
          </p>
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="svc-section svc-section-alt">
        <div className="svc-container">
          <div className="svc-section-header">
            <p className="svc-eyebrow tone-warm">Optional extras</p>
            <h2><em>Add-ons.</em></h2>
            <p>Layer any of these onto your package. Final pricing confirmed before anything is added.</p>
          </div>
          <div className="svc-addons-grid">
            {/* Top row: 3 standard cards */}
            {topAddons.map((item) => (
              <AddonCard key={item.name} item={item} isHighlight={false} />
            ))}

            {/* Top highlight banner: AI chatbot */}
            {topHighlight && (
              <AddonCard key={topHighlight.name} item={topHighlight} isHighlight={true} />
            )}

            {/* Middle row: remaining standard cards */}
            <div className="svc-addons-bottom">
              {middleAddons.map((item) => (
                <AddonCard key={item.name} item={item} isHighlight={false} />
              ))}
            </div>

            {/* Middle highlight banner: POS setup */}
            {middleHighlight && (
              <AddonCard key={middleHighlight.name} item={middleHighlight} isHighlight={true} />
            )}

            {/* Bottom highlight banner: Custom booking system */}
            {bottomHighlight && (
              <AddonCard key={bottomHighlight.name} item={bottomHighlight} isHighlight={true} />
            )}
          </div>
          <p className="svc-addons-disclosure">
            All add-on pricing is approximate and depends on your specific setup.<br />
            You&apos;ll always approve a clear quote before anything is added.
          </p>
        </div>
      </section>

      {/* ESTIMATOR */}
      <Estimator />

      {/* MAINTENANCE PLANS */}
      <section className="svc-section svc-section-alt">
        <div className="svc-container">
          <div className="svc-section-header">
            <p className="svc-eyebrow tone-sage">After launch</p>
            <h2>Maintenance <em>plans.</em></h2>
            <p>Optional monthly support. Cancel anytime.</p>
          </div>
          <div className="svc-maintenance-strip">
            {MAINTENANCE_PLANS.map((plan) => (
              <div key={plan.name} className="svc-maintenance-row">
                <div className="svc-maintenance-row-head">
                  <h4>{plan.name}</h4>
                  <div className="svc-maintenance-row-price">
                    <span className="svc-maintenance-price">{plan.price}</span>
                    <span className="svc-maintenance-price-sub">/ month</span>
                  </div>
                </div>
                <p className="svc-maintenance-tagline">{plan.tagline}</p>
                <ul className="svc-maintenance-bullets">
                  {plan.bullets.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <p className="svc-maintenance-note">
            A &ldquo;content update&rdquo; includes changes like updating text, swapping photos, adjusting pricing, or renaming a section. Anything outside that scope is quoted separately before any work begins.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="svc-cta">
        <div className="svc-cta-grid" aria-hidden="true" />
        <div className="svc-cta-glow" aria-hidden="true" />
        <div className="svc-container-narrow svc-cta-inner">
          <h2>Let&apos;s talk about your <em>project.</em></h2>
          <p>
            Tell us what you&apos;re building and what you need. No pressure, no obligation. Just a conversation about your project. Based in New York, Layer &amp; Co. works with small businesses remotely, wherever you are.
          </p>
          <div className="svc-cta-actions">
            <Link to="/contact" className="svc-btn svc-btn-primary svc-btn-arrow">
              Get in touch
            </Link>
            <Link to="/" className="svc-btn svc-btn-secondary">
              Take the quiz
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}

export default Services;